import { useEffect } from 'react';

import { logError } from '@/utils/errorLogger';

/**
 * Custom hook to clean up service workers on app initialization
 * This ensures no stale service workers remain from previous deployments
 */
export const useServiceWorkerCleanup = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cleanupServiceWorkers = async (): Promise<void> => {
      if (!('serviceWorker' in navigator)) {
        return;
      }

      try {
        // Get all service worker registrations
        const registrations = await navigator.serviceWorker.getRegistrations();

        if (registrations.length > 0) {
          // Unregister each service worker
          const unregisterPromises = registrations.map(async registration => {
            try {
              await registration.unregister();
            } catch (error) {
              logError(error, {
                source: 'useServiceWorkerCleanup:unregister',
                context: { reason: 'service_worker_unregister_failed' },
                severity: 'warn',
              });
            }
          });

          await Promise.all(unregisterPromises);
        }

        // Clear all caches if Cache API is available
        if ('caches' in window) {
          try {
            const cacheNames = await caches.keys();
            if (cacheNames.length > 0) {
              const deletePromises = cacheNames.map(async cacheName => {
                try {
                  await caches.delete(cacheName);
                } catch (error) {
                  logError(error, {
                    source: 'useServiceWorkerCleanup:deleteCache',
                    context: { reason: 'cache_delete_failed', cacheName },
                    severity: 'warn',
                  });
                }
              });
              await Promise.all(deletePromises);
            }
          } catch (error) {
            logError(error, {
              source: 'useServiceWorkerCleanup:cachesCleanup',
              context: { reason: 'caches_cleanup_failed' },
              severity: 'warn',
            });
          }
        }
      } catch (error) {
        logError(error, {
          source: 'useServiceWorkerCleanup',
          context: { reason: 'service_worker_cleanup_failed' },
          severity: 'warn',
        });
      }
    };

    // Execute service worker cleanup
    void cleanupServiceWorkers();
  }, []);
};
